import { useEffect, useRef } from 'react';

/**
 * Google Sign-In button — usa GIS oficial (branded). Lo dejamos rectangular
 * para que ocupe toda el área renderizada por Google y no se vea un
 * rectángulo de fondo alrededor de un pill.
 */

type Props = {
  onCredential: (idToken: string) => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (r: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function GoogleSignInButton({ onCredential }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    if (!clientId) {
      console.warn('VITE_GOOGLE_CLIENT_ID no está seteado');
      return;
    }

    function tryInit() {
      if (initialized.current || !window.google?.accounts?.id || !ref.current) return;
      initialized.current = true;
      window.google.accounts.id.initialize({
        client_id: clientId!,
        callback: (r) => onCredential(r.credential),
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(ref.current, {
        theme: 'filled_blue',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 320,
      });
    }

    tryInit();
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        tryInit();
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [onCredential]);

  return (
    <div className="inline-flex justify-center w-full">
      <div ref={ref} className="overflow-hidden rounded-lg" />
    </div>
  );
}
